import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';
import { OFFER_AGE_BUCKETS, JAMAIS_OFFRE_BUCKET, isDormant } from '../utils/stockVoOfferAge';

// Onglet « 📊 Tableau de bord » de Stock VO — synthèse visuelle du stock VO
// croisé avec les offres (cf. StockVoSuivi.js pour le détail filtrable/triable).
// Pas de graphique d'évolution : chaque import Offres CRM360 remplace tout
// l'historique (snapshot), il n'y a pas de série temporelle exploitable.

const STOCK_AGE_BUCKETS = [
  { key: '0-30',    label: '< 30 j',    test: j => j < 30 },
  { key: '30-90',   label: '30-90 j',   test: j => j >= 30 && j < 90 },
  { key: '90-180',  label: '90-180 j',  test: j => j >= 90 && j < 180 },
  { key: '180+',    label: '> 180 j',   test: j => j >= 180 },
];

const fmtMoney0 = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);

function KpiCard({ label, value, sublabel, accent }) {
  return (
    <div className={`svs-kpi-card ${accent ? `svs-kpi-${accent}` : ''}`}>
      <div className="svs-kpi-label">{label}</div>
      <div className="svs-kpi-value">{value}</div>
      {sublabel && <div className="svs-kpi-sub">{sublabel}</div>}
    </div>
  );
}

function ChartCard({ title, children, height = 220 }) {
  return (
    <div className="svs-chart-card">
      <h3>{title}</h3>
      <ResponsiveContainer width="100%" height={height}>
        {children}
      </ResponsiveContainer>
    </div>
  );
}

export default function StockVoDashboard({ enriched, loading, concList = [], concFilter = '', setConcFilter = () => {} }) {
  const scoped = useMemo(
    () => concFilter ? enriched.filter(r => String(r.concession_id) === String(concFilter)) : enriched,
    [enriched, concFilter]
  );

  const stats = useMemo(() => {
    const enriched = scoped; // scope tous les calculs sur la concession filtrée (ou tout, si aucun filtre)
    const total = enriched.length;

    // Répartition par tranche d'ancienneté de DERNIÈRE OFFRE (demande explicite).
    const parDormance = OFFER_AGE_BUCKETS.map(b => {
      const items = enriched.filter(r => r.jours_offre !== null && b.test(r.jours_offre));
      return { key: b.key, label: b.label, color: b.color, nb: items.length, valeur: items.reduce((s, r) => s + (parseFloat(r.prix_conseille) || 0), 0) };
    });
    const jamaisOffre = enriched.filter(r => r.jours_offre === null);
    parDormance.push({
      key: JAMAIS_OFFRE_BUCKET.key, label: JAMAIS_OFFRE_BUCKET.label, color: JAMAIS_OFFRE_BUCKET.color,
      nb: jamaisOffre.length, valeur: jamaisOffre.reduce((s, r) => s + (parseFloat(r.prix_conseille) || 0), 0),
    });

    // Répartition par tranche d'ancienneté DE STOCK (dimension distincte : un
    // véhicule récent peut déjà être dormant, un vieux véhicule peut avoir une
    // offre fraîche — les deux signaux se lisent ensemble, pas l'un pour l'autre).
    const parAncienneteStock = STOCK_AGE_BUCKETS.map(b => ({
      key: b.key,
      label: b.label,
      nb: enriched.filter(r => r.jours_stock !== null && b.test(r.jours_stock)).length,
    }));

    // Par concession : volume + dormance, triée par % dormant décroissant
    // (fait remonter le site le plus en difficulté en premier).
    const concMap = new Map();
    enriched.forEach(r => {
      const key = r.concession_nom || 'Non rattaché';
      if (!concMap.has(key)) concMap.set(key, { label: key, nb: 0, dormants: 0 });
      const c = concMap.get(key);
      c.nb++;
      if (isDormant(r.jours_offre)) c.dormants++;
    });
    const parConcession = Array.from(concMap.values())
      .map(c => ({ ...c, pct: c.nb ? Math.round(c.dormants / c.nb * 100) : 0 }))
      .sort((a, b) => b.pct - a.pct);

    // Top marques du stock dormant (dormant + jamais offert) — regroupe le
    // reste sous « Autres » au-delà de 8 marques pour rester lisible.
    const dormantRows = enriched.filter(r => isDormant(r.jours_offre));
    const marqueMap = new Map();
    dormantRows.forEach(r => {
      const key = r.marque || 'Non renseignée';
      marqueMap.set(key, (marqueMap.get(key) || 0) + 1);
    });
    const marquesTriees = Array.from(marqueMap.entries()).map(([label, nb]) => ({ label, nb })).sort((a, b) => b.nb - a.nb);
    const topMarques = marquesTriees.slice(0, 8);
    const resteMarques = marquesTriees.slice(8).reduce((s, m) => s + m.nb, 0);
    if (resteMarques > 0) topMarques.push({ label: 'Autres', nb: resteMarques });

    const valeurDormante = dormantRows.reduce((s, r) => s + (parseFloat(r.prix_conseille) || 0), 0);
    const joursConnus = enriched.filter(r => r.jours_offre !== null).map(r => r.jours_offre);
    const ageMoyen = joursConnus.length ? Math.round(joursConnus.reduce((a, b) => a + b, 0) / joursConnus.length) : null;

    return {
      total,
      nbDormant: dormantRows.length,
      pctDormant: total ? Math.round(dormantRows.length / total * 100) : 0,
      valeurDormante,
      ageMoyen,
      parDormance,
      parAncienneteStock,
      parConcession,
      topMarques,
    };
  }, [scoped]);

  const concListValid = (concList || []).filter(c => c.id && c.nom);

  if (loading) return <div className="ddb-loading">Chargement...</div>;
  if (!enriched.length) return <div className="ddb-empty">Aucun véhicule en stock VO.</div>;

  return (
    <div className="svs-dashboard">
      {concListValid.length > 1 && (
        <div className="page-toolbar svs-dashboard-toolbar">
          <select className="page-select" value={concFilter} onChange={e => setConcFilter(e.target.value)}>
            <option value="">Toutes concessions</option>
            {concListValid.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
          </select>
        </div>
      )}

      {!scoped.length ? (
        <div className="ddb-empty">Aucun véhicule en stock VO pour cette concession.</div>
      ) : (<>
      <div className="svs-kpi-row">
        <KpiCard label="Stock VO total" value={stats.total} />
        <KpiCard
          label="🔴 Stock dormant / jamais offert"
          value={stats.nbDormant}
          sublabel={`${stats.pctDormant} % du stock`}
          accent="dormant"
        />
        <KpiCard
          label="Valeur immobilisée (stock dormant)"
          value={fmtMoney0(stats.valeurDormante)}
          sublabel="Prix conseillé cumulé, véhicules dormants/jamais offerts"
        />
        <KpiCard
          label="Âge moyen depuis dernière offre"
          value={stats.ageMoyen !== null ? `${stats.ageMoyen} j` : '—'}
        />
      </div>

      <div className="svs-charts-grid">
        <ChartCard title="📅 Répartition par ancienneté de dernière offre" height={260}>
          <BarChart data={stats.parDormance} layout="vertical" margin={{ top: 4, right: 40, left: 8, bottom: 0 }}>
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="label" width={110} tick={{ fontSize: 12, fill: 'var(--text-secondary)' }} />
            <Tooltip
              cursor={{ fill: 'var(--bg-hover)' }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const p = payload[0].payload;
                return (
                  <div className="svs-tooltip">
                    <div className="svs-tooltip-title">{p.label}</div>
                    <div className="svs-tooltip-row"><span>Véhicules</span><span>{p.nb}</span></div>
                    <div className="svs-tooltip-row"><span>Valeur</span><span>{fmtMoney0(p.valeur)}</span></div>
                  </div>
                );
              }}
            />
            <Bar dataKey="nb" radius={[0, 4, 4, 0]}>
              {stats.parDormance.map(d => <Cell key={d.key} fill={d.color} />)}
              <LabelList dataKey="nb" position="right" style={{ fontSize: 12, fill: 'var(--text-primary)', fontWeight: 600 }} />
            </Bar>
          </BarChart>
        </ChartCard>

        <ChartCard title="📦 Répartition par ancienneté de stock">
          <BarChart data={stats.parAncienneteStock} layout="vertical" margin={{ top: 4, right: 40, left: 8, bottom: 0 }}>
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="label" width={80} tick={{ fontSize: 12, fill: 'var(--text-secondary)' }} />
            <Tooltip
              cursor={{ fill: 'var(--bg-hover)' }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const p = payload[0].payload;
                return <div className="svs-tooltip"><div className="svs-tooltip-title">{p.label}</div><div className="svs-tooltip-row"><span>Véhicules</span><span>{p.nb}</span></div></div>;
              }}
            />
            <Bar dataKey="nb" fill="var(--color-accent)" radius={[0, 4, 4, 0]}>
              <LabelList dataKey="nb" position="right" style={{ fontSize: 12, fill: 'var(--text-primary)', fontWeight: 600 }} />
            </Bar>
          </BarChart>
        </ChartCard>

        <ChartCard title="🏢 % de stock dormant par concession" height={Math.max(180, stats.parConcession.length * 40 + 30)}>
          <BarChart data={stats.parConcession} layout="vertical" margin={{ top: 4, right: 50, left: 8, bottom: 0 }}>
            <XAxis type="number" hide domain={[0, 100]} />
            <YAxis type="category" dataKey="label" width={150} tick={{ fontSize: 12, fill: 'var(--text-secondary)' }} />
            <Tooltip
              cursor={{ fill: 'var(--bg-hover)' }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const p = payload[0].payload;
                return (
                  <div className="svs-tooltip">
                    <div className="svs-tooltip-title">{p.label}</div>
                    <div className="svs-tooltip-row"><span>Dormant</span><span>{p.dormants} / {p.nb}</span></div>
                    <div className="svs-tooltip-row"><span>Taux</span><span>{p.pct} %</span></div>
                  </div>
                );
              }}
            />
            <Bar dataKey="pct" fill="var(--color-accent)" radius={[0, 4, 4, 0]}>
              <LabelList dataKey="pct" position="right" formatter={v => `${v} %`} style={{ fontSize: 12, fill: 'var(--text-primary)', fontWeight: 600 }} />
            </Bar>
          </BarChart>
        </ChartCard>

        <ChartCard title="🚗 Marques les plus représentées dans le stock dormant" height={Math.max(180, stats.topMarques.length * 32 + 30)}>
          {stats.topMarques.length === 0 ? (
            <div className="ddb-empty">Aucun véhicule dormant.</div>
          ) : (
            <BarChart data={stats.topMarques} layout="vertical" margin={{ top: 4, right: 40, left: 8, bottom: 0 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="label" width={130} tick={{ fontSize: 12, fill: 'var(--text-secondary)' }} />
              <Tooltip
                cursor={{ fill: 'var(--bg-hover)' }}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const p = payload[0].payload;
                  return <div className="svs-tooltip"><div className="svs-tooltip-title">{p.label}</div><div className="svs-tooltip-row"><span>Véhicules dormants</span><span>{p.nb}</span></div></div>;
                }}
              />
              <Bar dataKey="nb" fill="var(--color-error)" radius={[0, 4, 4, 0]}>
                <LabelList dataKey="nb" position="right" style={{ fontSize: 12, fill: 'var(--text-primary)', fontWeight: 600 }} />
              </Bar>
            </BarChart>
          )}
        </ChartCard>
      </div>
      </>)}
    </div>
  );
}
